import { RefObject, useEffect } from 'react'
import cls from 'classnames'
import { CallTypeEnum } from '@medentee/enums'
import { useTranslation } from 'react-i18next'

import { CallStatus, IconButton, TUseCallCoreOptions } from 'App/components'
import { ECallConnectionQuality } from 'App/components/Chat/Calls/LiveKitRoom'
import { EModalComponents, TCallOutgoingFooterContainerProps } from 'App/containers'
import { useAdaptiveLayout, useCloseModal } from 'App/hooks'
import { EIconSize } from 'enums'
import { ReactComponent as AccountPlusOutlineIcon } from 'assets/icons/AccountPlusOutline.svg'
import { ReactComponent as MessageTextIcon } from 'assets/icons/MessageText.svg'

import styles from './CallOutgoingFooter.module.scss'

export type TCallOutgoingFooterProps = TCallOutgoingFooterContainerProps &
  Pick<TUseCallCoreOptions, 'type'> & {
    isFullscreen: boolean
    isAnsweredCall: boolean
    connectionQuality?: ECallConnectionQuality
    onFullscreen: () => void
    isCallOwner: boolean
    callId: string
    fixed: boolean

    rootRef?: null | RefObject<HTMLElement>
    cancelDraggableClassName?: string
  }

export const CallOutgoingFooter = ({
  isFullscreen,
  isAnsweredCall,
  connectionQuality,
  isCallOwner,
  caseId,
  chatType,
  callId,
  startTime,
  cancelDraggableClassName,
  rootRef,
  fixed,
  onFullscreen,
  showModal
}: TCallOutgoingFooterProps) => {
  const { isMobile, isDesktop } = useAdaptiveLayout()

  const { t } = useTranslation()

  const hideCurrentModal = useCloseModal([EModalComponents.ADD_TO_GROUP_CALL_DIALOG])

  const isButtonSizeSM = isDesktop && !isFullscreen

  const shouldShowAddButton = caseId && isCallOwner && chatType === CallTypeEnum.CASE_GROUP
  const shouldShowChatButton =
    !(shouldShowAddButton && !isFullscreen) &&
    (chatType === CallTypeEnum.CASE_GROUP || chatType === CallTypeEnum.CASE_MEETING)

  const getContainer = (isFullscreen && rootRef?.current) || undefined

  const handleAddMembers = () =>
    caseId &&
    showModal({
      modalTitle: t('modal.addGroupCallMember.title_add'),
      modalType: EModalComponents.ADD_TO_GROUP_CALL_DIALOG,
      modalProps: {
        callId,
        caseId
      },
      getContainer
    })

  useEffect(
    () => () => {
      hideCurrentModal()
    },
    [hideCurrentModal, isFullscreen]
  )

  const buttonClasses = cls(
    styles.button,
    cancelDraggableClassName,
    isButtonSizeSM && styles.buttonSmall
  )

  return (
    <div
      className={cls(styles.root, { [styles.static]: !fixed, [styles.fullscreen]: isFullscreen })}
    >
      {!isMobile && (
        <CallStatus
          isAnsweredCall={isAnsweredCall}
          startTime={startTime}
          connectionQuality={connectionQuality}
        />
      )}

      <div className={styles.center}>
        {shouldShowAddButton && (
          <IconButton
            toolTip={t('chat.call.toolbar.addMembers')}
            iconComponent={<AccountPlusOutlineIcon />}
            iconSize={EIconSize.CUSTOM}
            onClick={handleAddMembers}
            classes={{ root: buttonClasses }}
          />
        )}

        {shouldShowChatButton && fixed && !isFullscreen && (
          <IconButton
            toolTip={t('chat.call.toolbar.chat')}
            iconComponent={<MessageTextIcon />}
            iconSize={EIconSize.CUSTOM}
            onClick={onFullscreen}
            classes={{ root: buttonClasses }}
          />
        )}
      </div>
    </div>
  )
}
